import React, { useCallback, useContext } from 'react';
import { TranslatorContext } from '@jutro/locale';
import { CurrencyValue, Link } from '@jutro/components';

import { BreakpointTrackerContext } from '@jutro/layout';
import messages from './SgRenewalSphUpgradeTable.messages';

const BUILDINGS_SUM_INSURED_MAP = {
    up_to_five_hundred_thousand: '£500,000',
    more_than_five_hundred_thousand: '£500,000',
    up_to_one_million: '£1,000,000',
    more_than_one_million: '' // this case will be tailormade policy that cannot be upgraded
};
const CONTENTS_SUM_INSURED_MAP = {
    up_to_ten_thousand: '£10,000',
    up_to_twenty_five_thousand: '£25,000',
    up_to_forty_thousand: '£40,000',
    up_to_fifty_thousand: '£50,000',
    up_to_seventy_five_thousand: '£75,000',
    up_to_one_hundred_thousand: '£100,000',
    more_than_one_hundred_thousand: '', // this case will be tailormade policy that cannot be upgraded
};

function SgRenewalSphUpgradeTable(props) {
    const {
        id,
        renewal,
        showTermsAndConditions
    } = props;

    const {
        renewalUpgradeData
    } = renewal;

    const translator = useContext(TranslatorContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const isMobile = breakpoint === 'phoneWide' || breakpoint === 'phone';

    const getBuildingsSumInsured = useCallback(() => {
        return BUILDINGS_SUM_INSURED_MAP[renewal.renewalUpgradeData.buildingCoverSumInsured];
    }, [renewal]);

    const getContentsSumInsured = useCallback(() => {
        return CONTENTS_SUM_INSURED_MAP[renewal.renewalUpgradeData.contentsCoverSumInsured];
    }, [renewal]);

    return (
        <>
            {!isMobile
            && (
                <table className="sgCoverComparisonTable">
                    <thead>
                        <tr>
                            <th width="60%">{translator(messages.CoverAndBenefits)}</th>
                            <th width="20%">
                                {translator(messages.SagaSelect)}
                                <br />
                                {translator(messages.CurrentCoverLevel)}
                            </th>
                            <th width="20%">{translator(messages.SagaPlus)}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr id={`fixedPriceRow_${id}`}>
                            <td>
                                <strong>{translator(messages.ThreeYearFixedPrice)}</strong>
                                <div>
                                    <ul>
                                        <li>{translator(messages.Bullet1)}</li>
                                        <li>{translator(messages.Bullet2)}</li>
                                    </ul>
                                </div>
                                <Link href="/" onClick={showTermsAndConditions}>
                                    {translator(messages.TnCApply)}
                                </Link>
                            </td>
                            <td>
                                <div className="cross"><span>{translator(messages.No)}</span></div>
                            </td>
                            <td>
                                <div className="tick"><span>{translator(messages.Yes)}</span></div>
                            </td>
                        </tr>

                        <tr id={`coverSumInsuredRow_${id}`}>
                            <td>
                                <span>{translator(messages.UpTo)}</span>
                                {renewalUpgradeData.hasBuildingCover
                                && (
                                    <strong>
                                        { renewalUpgradeData.buildingCoverSumInsured
                                            ? (
                                                <span id={`buildingsSumInsured_${id}`}>
                                                    {getBuildingsSumInsured()}
                                                </span>
                                            )
                                            : (
                                                <span id={`buildingsSumInsured_${id}`}>
                                                    <CurrencyValue
                                                        currency="gbp"
                                                        currencyDisplay="symbol"
                                                        id="CurrencyValue"
                                                        placeholder="-"
                                                        showGrouping
                                                        tag="span"
                                                        value={renewalUpgradeData.buildingCoverSpecifiedSumInsured.amount}
                                                    />
                                                </span>
                                            )
                                        }
                                        {translator(messages.BuildingCover)}
                                    </strong>
                                )
                                }
                                {renewalUpgradeData.hasBuildingCover && renewalUpgradeData.hasContentsCover
                                && (
                                    <span>
                                        {translator(messages.AndUpTo)}
                                    </span>
                                )}
                                {renewalUpgradeData.hasContentsCover
                                && (
                                    <strong>
                                        <span id={`contentsSumInsured_${id}`}>{getContentsSumInsured()}</span>
                                        {translator(messages.ContentsCover)}
                                    </strong>
                                )
                                }
                            </td>
                            <td>
                                <div className="tick"><span>{translator(messages.Yes)}</span></div>
                            </td>
                            <td>
                                <div className="tick"><span>{translator(messages.Yes)}</span></div>
                            </td>
                        </tr>

                        <tr id={`AdCoverRow_${id}`}>
                            <td className="text-content">
                                <strong>{translator(messages.ADinHome)}</strong>
                                {translator(messages.ForIncidents)}
                                {renewalUpgradeData.hasContentsCover && !renewalUpgradeData.hasBuildingCover
                                && <span>{translator(messages.DroppingLaptop)}</span>
                                }
                                {renewalUpgradeData.hasBuildingCover && renewalUpgradeData.hasContentsCover
                                && <span>{translator(messages.CombinedLaptop)}</span>
                                }
                                {renewalUpgradeData.hasBuildingCover && !renewalUpgradeData.hasContentsCover
                                && <span>{translator(messages.HotPan)}</span>
                                }
                            </td>
                            {renewalUpgradeData.hasContentsAD && renewalUpgradeData.hasBuildingsAD
                            && <td><div className="tick"><span>{translator(messages.Yes)}</span></div></td>
                            }
                            {!renewalUpgradeData.hasContentsAD && !renewalUpgradeData.hasBuildingsAD
                            && <td><span>{translator(messages.NotSelected)}</span></td>
                            }
                            {renewalUpgradeData.hasContentsAD && !renewalUpgradeData.hasBuildingsAD
                            && <td><span>{translator(messages.ContentsOnly)}</span></td>
                            }
                            {renewalUpgradeData.hasBuildingsAD && !renewalUpgradeData.hasContentsAD
                            && <td><span>{translator(messages.BuildingsOnly)}</span></td>
                            }
                            <td>
                                <div className="tick"><span>{translator(messages.Yes)}</span></div>
                            </td>
                        </tr>

                        {renewalUpgradeData.hasContentsCover
                        && (
                            <tr id={`PBRow_${id}`}>
                                <td className="text-content">
                                    <strong>{translator(messages.PersonalBelongingsCover)}</strong>
                                    {translator(messages.ForItemsAwayFromHome)}
                                </td>
                                {renewalUpgradeData.hasPersonalBelongingsCover
                                    ? (
                                        <td>
                                            <div className="tick">
                                                <span>{translator(messages.Yes)}</span>
                                            </div>
                                            <span>(optional)</span>
                                        </td>
                                    )
                                    : (
                                        <td>
                                            <span>{translator(messages.NotSelected)}</span>
                                        </td>
                                    )
                                }
                                <td>
                                    <div className="tick"><span>{translator(messages.Yes)}</span></div>
                                </td>
                            </tr>
                        )
                        }

                        <tr id={`ReplacementSetsOfItemsRow_${id}`}>
                            <td className="text-content">
                                <strong>{translator(messages.ReplacementOfSets)}</strong>
                                {translator(messages.SuchAs)}
                                { renewalUpgradeData.hasContentsCover && !renewalUpgradeData.hasBuildingCover
                                && (
                                    <span>
                                        {translator(messages.Earrings)}
                                        {translator(messages.IfCannotReplace)}
                                    </span>
                                )
                                }
                                { renewalUpgradeData.hasBuildingCover && renewalUpgradeData.hasContentsCover
                                && (
                                    <span>
                                        {translator(messages.EarringsOrCabinets)}
                                        {translator(messages.IfCannotReplace)}
                                    </span>
                                )
                                }
                                { renewalUpgradeData.hasBuildingCover && !renewalUpgradeData.hasContentsCover
                                && (
                                    <span>
                                        {translator(messages.Cabinets)}
                                        {translator(messages.IfCannotReplace)}
                                    </span>
                                )
                                }
                            </td>
                            <td>
                                <div className="cross"><span>{translator(messages.No)}</span></div>
                            </td>
                            <td>
                                <div className="tick"><span>{translator(messages.Yes)}</span></div>
                            </td>
                        </tr>

                        { renewalUpgradeData.hasBuildingCover
                        && (
                            <tr id={`traceAndAccessRow_${id}`}>
                                <td className="text-content">
                                    <strong>{translator(messages.TraceAndAccess)}</strong>
                                    {translator(messages.SourceOfLeak)}
                                </td>
                                <td>
                                    <span>{translator(messages.UpToFiveK)}</span>
                                </td>
                                <td>
                                    {translator(messages.UpTo)}
                                    {renewalUpgradeData.buildingCoverSumInsured
                                        ? <span>{ getBuildingsSumInsured() }</span>
                                        : (
                                            <span>
                                                <CurrencyValue
                                                    currency="gbp"
                                                    currencyDisplay="symbol"
                                                    id="CurrencyValue"
                                                    placeholder="-"
                                                    showGrouping
                                                    tag="span"
                                                    value={renewalUpgradeData.buildingCoverSpecifiedSumInsured.amount}
                                                />
                                            </span>
                                        )
                                    }
                                </td>
                            </tr>
                        )
                        }

                        <tr id={`scratchingRow_${id}`}>
                            <td className="text-content">
                                <strong>{translator(messages.Scratching)}</strong>
                                {translator(messages.ForExample)}
                                { renewalUpgradeData.hasContentsCover
                                && <span>{translator(messages.AccidentallyScratchingTable)}</span>
                                }
                                { renewalUpgradeData.hasBuildingCover && renewalUpgradeData.hasContentsCover
                                && <span>{translator(messages.OrConjunction)}</span>
                                }
                                {renewalUpgradeData.hasBuildingCover
                                && <span>{translator(messages.MovingFurniture)}</span>
                                }
                            </td>
                            <td>
                                <div className="cross"><span>{translator(messages.No)}</span></div>
                            </td>
                            <td>
                                <div className="tick"><span>{translator(messages.Yes)}</span></div>
                            </td>
                        </tr>

                        { renewalUpgradeData.hasContentsCover
                        && (
                            <tr id={`petsDamageRow_${id}`}>
                                <td className="text-content">
                                    <strong>{translator(messages.PetsDamage)}</strong>
                                    {translator(messages.ChewsSofa)}
                                </td>
                                <td>
                                    <div className="cross"><span>{translator(messages.No)}</span></div>
                                </td>
                                <td>
                                    <div className="tick"><span>{translator(messages.Yes)}</span></div>
                                </td>
                            </tr>
                        )
                        }

                    </tbody>
                </table>
            )
            }
            {isMobile
            && (
                <table className="sgCoverComparisonTable sgCoverComparisonTableMobile">
                    <thead>
                        <tr>
                            <th width="50%">
                                {translator(messages.SagaSelect)}
                                <br />
                                {translator(messages.CurrentCoverLevel)}
                            </th>
                            <th width="50%">{translator(messages.SagaPlus)}</th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr id={`fixedPriceRow_${id}`}>
                            <td colSpan="2" className="sgComparisonTableMobileInfoContainer">
                                <strong>{translator(messages.ThreeYearFixedPrice)}</strong>
                                <div>
                                    <ul>
                                        <li>{translator(messages.Bullet1)}</li>
                                        <li>{translator(messages.Bullet2)}</li>
                                    </ul>
                                </div>
                                <Link href="/" onClick={showTermsAndConditions}>
                                    {translator(messages.TnCApply)}
                                </Link>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <div className="cross"><span>{translator(messages.No)}</span></div>
                            </td>
                            <td>
                                <div className="tick"><span>{translator(messages.Yes)}</span></div>
                            </td>
                        </tr>

                        <tr id={`coverSumInsuredRow_${id}`}>
                            <td colSpan="2" className="sgComparisonTableMobileInfoContainer">
                                <span>{translator(messages.UpTo)}</span>
                                {renewalUpgradeData.hasBuildingCover
                            && (
                                <strong>
                                    { renewalUpgradeData.buildingCoverSumInsured
                                        ? (
                                            <span id={`buildingsSumInsured_${id}`}>
                                                {getBuildingsSumInsured()}
                                            </span>
                                        )
                                        : (
                                            <span id={`buildingsSumInsured_${id}`}>
                                                <CurrencyValue
                                                    currency="gbp"
                                                    currencyDisplay="symbol"
                                                    id="CurrencyValue"
                                                    placeholder="-"
                                                    showGrouping
                                                    tag="span"
                                                    value={renewalUpgradeData.buildingCoverSpecifiedSumInsured.amount}
                                                />
                                            </span>
                                        )
                                    }
                                    {translator(messages.BuildingCover)}
                                </strong>
                            )
                                }
                                {renewalUpgradeData.hasBuildingCover && renewalUpgradeData.hasContentsCover
                            && (
                                <span>
                                    {translator(messages.AndUpTo)}
                                </span>
                            )}
                                {renewalUpgradeData.hasContentsCover
                            && (
                                <strong>
                                    <span id={`contentsSumInsured_${id}`}>{getContentsSumInsured()}</span>
                                    {translator(messages.ContentsCover)}
                                </strong>
                            )
                                }
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <div className="tick"><span>{translator(messages.Yes)}</span></div>
                            </td>
                            <td>
                                <div className="tick"><span>{translator(messages.Yes)}</span></div>
                            </td>
                        </tr>

                        <tr id={`AdCoverRow_${id}`}>
                            <td colSpan="2" className="sgComparisonTableMobileInfoContainer">
                                <strong>{translator(messages.ADinHome)}</strong>
                                {translator(messages.ForIncidents)}
                                {renewalUpgradeData.hasContentsCover && !renewalUpgradeData.hasBuildingCover
                            && <span>{translator(messages.DroppingLaptop)}</span>
                                }
                                {renewalUpgradeData.hasBuildingCover && renewalUpgradeData.hasContentsCover
                            && <span>{translator(messages.CombinedLaptop)}</span>
                                }
                                {renewalUpgradeData.hasBuildingCover && !renewalUpgradeData.hasContentsCover
                            && <span>{translator(messages.HotPan)}</span>
                                }
                            </td>
                        </tr>
                        <tr>
                            {renewalUpgradeData.hasContentsAD && renewalUpgradeData.hasBuildingsAD
                        && <td><div className="tick"><span>{translator(messages.Yes)}</span></div></td>
                            }
                            {!renewalUpgradeData.hasContentsAD && !renewalUpgradeData.hasBuildingsAD
                        && <td><span>{translator(messages.NotSelected)}</span></td>
                            }
                            {renewalUpgradeData.hasContentsAD && !renewalUpgradeData.hasBuildingsAD
                        && <td><span>{translator(messages.ContentsOnly)}</span></td>
                            }
                            {renewalUpgradeData.hasBuildingsAD && !renewalUpgradeData.hasContentsAD
                        && <td><span>{translator(messages.BuildingsOnly)}</span></td>
                            }
                            <td>
                                <div className="tick"><span>{translator(messages.Yes)}</span></div>
                            </td>
                        </tr>

                        {renewalUpgradeData.hasContentsCover
                && (
                    <>
                        <tr id={`PBRow_${id}`}>
                            <td colSpan="2" className="sgComparisonTableMobileInfoContainer">
                                <strong>{translator(messages.PersonalBelongingsCover)}</strong>
                                {translator(messages.ForItemsAwayFromHome)}
                            </td>
                        </tr>
                        <tr>
                            {renewalUpgradeData.hasPersonalBelongingsCover
                                ? (
                                    <td>
                                        <div className="tick">
                                            <span>{translator(messages.Yes)}</span>
                                        </div>
                                        <span>(optional)</span>
                                    </td>
                                )
                                : (
                                    <td>
                                        <span>{translator(messages.NotSelected)}</span>
                                    </td>
                                )
                            }
                            <td>
                                <div className="tick"><span>{translator(messages.Yes)}</span></div>
                            </td>
                        </tr>
                    </>
                )
                        }

                        <tr id={`ReplacementSetsOfItemsRow_${id}`}>
                            <td className="text-content" colSpan="2">
                                <strong>{translator(messages.ReplacementOfSets)}</strong>
                                {translator(messages.SuchAs)}
                                { renewalUpgradeData.hasContentsCover && !renewalUpgradeData.hasBuildingCover
                            && (
                                <span>
                                    {translator(messages.Earrings)}
                                    {translator(messages.IfCannotReplace)}
                                </span>
                            )
                                }
                                { renewalUpgradeData.hasBuildingCover && renewalUpgradeData.hasContentsCover
                            && (
                                <span>
                                    {translator(messages.EarringsOrCabinets)}
                                    {translator(messages.IfCannotReplace)}
                                </span>
                            )
                                }
                                { renewalUpgradeData.hasBuildingCover && !renewalUpgradeData.hasContentsCover
                            && (
                                <span>
                                    {translator(messages.Cabinets)}
                                    {translator(messages.IfCannotReplace)}
                                </span>
                            )
                                }
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <div className="cross"><span>{translator(messages.No)}</span></div>
                            </td>
                            <td>
                                <div className="tick"><span>{translator(messages.Yes)}</span></div>
                            </td>
                        </tr>

                        { renewalUpgradeData.hasBuildingCover
                && (
                    <>
                        <tr id={`traceAndAccessRow_${id}`}>
                            <td colSpan="2" className="sgComparisonTableMobileInfoContainer">
                                <strong>{translator(messages.TraceAndAccess)}</strong>
                                {translator(messages.SourceOfLeak)}
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <span>{translator(messages.UpToFiveK)}</span>
                            </td>
                            <td>
                                {translator(messages.UpTo)}
                                {renewalUpgradeData.buildingCoverSumInsured
                                    ? <span>{ getBuildingsSumInsured() }</span>
                                    : (
                                        <span>
                                            <CurrencyValue
                                                currency="gbp"
                                                currencyDisplay="symbol"
                                                id="CurrencyValue"
                                                placeholder="-"
                                                showGrouping
                                                tag="span"
                                                value={renewalUpgradeData.buildingCoverSpecifiedSumInsured.amount}
                                            />
                                        </span>
                                    )
                                }
                            </td>
                        </tr>
                    </>
                )
                        }

                        <tr id={`scratchingRow_${id}`}>
                            <td colSpan="2" className="sgComparisonTableMobileInfoContainer">
                                <strong>{translator(messages.Scratching)}</strong>
                                {translator(messages.ForExample)}
                                { renewalUpgradeData.hasContentsCover
                            && <span>{translator(messages.AccidentallyScratchingTable)}</span>
                                }
                                { renewalUpgradeData.hasBuildingCover && renewalUpgradeData.hasContentsCover
                            && <span>{translator(messages.OrConjunction)}</span>
                                }
                                {renewalUpgradeData.hasBuildingCover
                            && <span>{translator(messages.MovingFurniture)}</span>
                                }
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <div className="cross"><span>{translator(messages.No)}</span></div>
                            </td>
                            <td>
                                <div className="tick"><span>{translator(messages.Yes)}</span></div>
                            </td>
                        </tr>

                        { renewalUpgradeData.hasContentsCover
                && (
                    <>
                        <tr id={`petsDamageRow_${id}`}>
                            <td colSpan="2" className="sgComparisonTableMobileInfoContainer">
                                <strong>{translator(messages.PetsDamage)}</strong>
                                {translator(messages.ChewsSofa)}
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <div className="cross"><span>{translator(messages.No)}</span></div>
                            </td>
                            <td>
                                <div className="tick"><span>{translator(messages.Yes)}</span></div>
                            </td>
                        </tr>
                    </>
                )
                        }

                    </tbody>
                </table>
            )
            }
        </>
    );
}

export default SgRenewalSphUpgradeTable;
